// modules
import React, { useMemo, useContext, useEffect, useRef } from "react";
import { Redirect, Route, Switch, useLocation, useHistory } from "react-router-dom";
// components
import LoadingPage from "../loadingPage/LoadingPage";
import TopBar from "../navbar/topBar/TopBar.component";
import Dashboard from "../dashboard/Dashboard.component";
import Support from "../support/Support.component";
import SignIn from "../auth/signIn/SignIn.component";
import SignUp from "../auth/signUp/SignUp.component";
import PasswordReset from "../auth/passwordReset/PasswordReset.component";
import Checkout from "../checkout/Checkout.component";
import ErrorPage from "../utils/errorPage/ErrorPage";
import CancelPlan from "../plans/CancelPlan.component";
import AreYouSure from "../plans/AreYouSure";
import PrivateRoute from "../privateRoute/PrivateRoute.component";
import StateContext from "../../context/StateContext";
import EditSegment from "../setup/EditSegment";
import ConnectFacebook from "../setup/ConnectFacebook";
import ConnectGoogle from "../setup/ConnectGoogle";
import ConnectPlatforms from "../setup/ConnectPlatforms";
import SubscribePlan from "../plans/SubscribePlan";
import Page404 from "../404/404.component";
import Introduction from "../introduction/Introduction.component"
// custom hooks
import usePageViews from "../../customHooks/usePageViews";
// services
import Firebase from "../../services/firebase";
import { createCustomer, getCustomerByMail, getPlansByMail, getSegmentsByMail, createPlan, getPageTitle, shouldGoToLoadingPage } from "../../services/globalService";
import { prepPlansMap, prepWithExtras } from "../../services/dataService";
// style
import { StyledInnerContainer, StyledMainContainer } from "./main.styled";
import { captureException } from "../../services/sentry"

const Main = () => {
	const location = useLocation();
	usePageViews(location); // sends the location to a hook that sends data to google analytics
	const { dispatch, ACTIONS, haveData, loading, reloadData, ...state } = useContext(StateContext);
	const history = useHistory();
	const customer = useRef(null);
	const plans = useRef(null);
	const segments = useRef(null);
	const haveSegments = useRef(false);
	const havePlans = useRef(false);
	const haveCustomer = useRef(false);
	const user = Firebase.getUser();

	// update tab title on route change
	useEffect(() => {
		document.title = getPageTitle(location.pathname);
	}, [location.pathname]);

	// init app on user change
	useEffect(() => {
		if (user) {
			const init = async () => {
				const done = await validateCustomer(); // get and check all data from FLAPI
				if (process.env.NODE_ENV !== "production") console.log("validateCustomer", done);
				if (done && shouldGoToLoadingPage(location)) history.push("/");
			};
			init();
		} else {
			dispatch({ type: ACTIONS.SET_LOADING, payload: false });
		}
	}, [user]);

	useEffect( () => {
		const reload = async () => {
			await checkPlans();
			history.push("/");
		}
		if(reloadData) {
			console.log("%creloading...","color: yellow");
			reload();
		}
	},[reloadData])

	const validateCustomer = async () => {
		if (state.haveCustomer && state.customer) {
			customer.current = state.customer;
			haveCustomer.current = true;
			return await checkPlans();
		}
		try {
			const res = await getCustomerByMail({ mail: user.email }); // get customer from FLAPI
			if (res?.data) {
				haveCustomer.current = true;
				customer.current = res.data;
				return await checkPlans();
			} else return false;
		} catch (e) {
			// handle responses
			const payload = {
				user: user,
				haveCustomer: false,
				customer: null,
				loading: false
			};
			if (e?.status === 404) {
				//user exists on firebase but not on fixel, registering user to fixel and creating trial plan
				captureException(e, "log", {}, { action: "validateCustomer", description: "user exists on firebase but not on fixel" }, { scope: "main.component.js" });
				const { data: newCustomer } = await createCustomer({ name: user.displayName, mail: user.email });
				await createPlan({ cust_id: newCustomer.cust_no, type: "Trial", name: "console", mail: newCustomer.mail });
				haveCustomer.current = true;
				customer.current = newCustomer;
				return await checkPlans();
			}
			console.error("Main.component.js ~ validateCustomer ~ error", e.message || e);
			dispatch({ type: ACTIONS.SET_STATE, payload });
			captureException(e, "error", {email: user.email, name: user.displayName}, { action: "validateCustomer", description: "validate customer failed" }, { scope: "main.component.js" });
			return history.push({ pathname: "/error-page", state: e });
		}
	};
	const checkPlans = async () => {
		if (state.havePlans && state.plans) {
			plans.current = state.plans;
			havePlans.current = true;
			return await checkSegments();
		}
		if (customer.current?.cust_no) {
			try {
				// get plans from FLAPI
				const res = await getPlansByMail({ mail: customer.current.mail });
				if (res?.data) {
					havePlans.current = true;
					plans.current = res.data;
					return await checkSegments();
				} else return false;
			} catch (e) {
				const payload = {
					user: user,
					haveCustomer: true,
					customer: customer.current,
					plans: null,
					havePlans: false,
					loading: false
				};
				console.error("Main.component.js ~ checkPlans ~ e", e.message || e);
				dispatch({ type: ACTIONS.SET_STATE, payload });
				captureException(e,
								 "error",
								 { email: user.email, name: user.displayName },
								 { action: "checkPlans", description: "an error occured while trying check plan" },
								 { scope: "main.component.js" });
				return history.push({ pathname: "/error-page", state: e });
			}
		}
	};

	const checkSegments = async () => {
		if (state.haveSegments && state.segments && state.segments?.length) {
			segments.current = state.segments;
			haveSegments.current = true;
			if (haveData) return true;
		}
		if (customer.current?.mail) {
			try {
				const payload = {
					customer: customer.current,
					haveCustomer: true,
					plans: plans.current,
					havePlans: true,

					segments: null,
					haveSegments: false,
					haveData: false,
					segMap: null,
					hitsMap: null,
					plansMap: null,
					audMap: null,
					websiteURL: "",
					selectedSegmentCode: "",

					showSpinner: false,
					loading: false
				};
				const resWithExtras = await getSegmentsByMail({ mail: customer.current?.mail, time_frame: "LAST_7_DAYS", extras: ["hits", "prevHits", "audiences"] });
				if (!resWithExtras?.data) {
					throw new Error("no data");
				}
				const { segMap, hitsMap, audMap, segmentsArray } = prepWithExtras({ segments: resWithExtras.data, time_frame: "LAST_7_DAYS" });
				if (!(segMap && hitsMap && audMap && segmentsArray)) {
					throw new Error("unable to process data");
				}
				payload.segments = segmentsArray;
				segments.current = segmentsArray;
				payload.haveSegments = true;
				haveSegments.current = true;
				payload.plansMap = prepPlansMap(plans.current);
				payload.segMap = segMap;
				payload.hitsMap = hitsMap;
				payload.audMap = audMap;
				payload.haveData = true;
				payload.selectedSegmentCode = Object.keys(segMap)[0];

				dispatch({ type: ACTIONS.SET_STATE, payload });
				return true;
			} catch (e) {
				const payload = {
					customer: customer.current,
					haveCustomer: true,
					plansMap: prepPlansMap(plans.current),
					plans: plans.current,
					havePlans: true,

					segments: null,
					haveSegments: false,
					haveData: false,
					segMap: null,
					hitsMap: null,
					audMap: null,
					websiteURL: "",
					selectedSegmentCode: "",

					showSpinner: false,
					loading: false
				};
				if (e.status === 404) {
					console.error("Main.component.js ~ checkSegments ~ e", e);
					dispatch({ type: ACTIONS.SET_STATE, payload });	
					captureException(
						e,
						"log",
						{ email: user.email, name: user.displayName },
						{ action: "checkSegments", description: "No Segments were found" },
						{ scope: "main.component.js" }
					);
					return false;
				}
				console.error("Main.component.js ~ checkSegments ~ e", e.message || e);
				dispatch({ type: ACTIONS.SET_STATE, payload });
				captureException(
					e,
					"error",
					{ email: user.email, name: user.displayName },
					{ action: "checkSegments", description: "App failed on segments retrieval" },
					{ scope: "main.component.js" }
				);
				return history.push({pathname: "/error-page",state: e});
			}
		}
	};

	const noNavbarPages = useMemo(() => {
		const noNavbarRoutes = ["login", "404", "start", "signup", "start/platform", "start/platform/shopify", "start/platform/wordpress", "error-page", "password-reset"];
		const noNavbarPages = ["/"];
		for (const route of noNavbarRoutes) {
			noNavbarPages.push(`/${route}`);
			noNavbarPages.push(`/${route}/`);
		}
		return noNavbarPages;
	}, []);

	const showNav = !noNavbarPages.includes(location.pathname);

	return (
		<div>
			<StyledMainContainer showNav={showNav}>
				{showNav && <TopBar/>}
				<StyledInnerContainer>
					<Switch>
						<Route
							exact
							path={"/"}
							render={(props) => (
								<LoadingPage
									{...props}
									loading={loading}
									user={user}
									haveData={haveData}
									haveSegments={haveSegments.current}
									havePlans={havePlans.current}
									haveCustomer={haveCustomer.current}
								/>
							)}
						/>
						<Route exact path="/error-page" component={ErrorPage} />
						<Route exact path="/404" component={Page404} />
						<PrivateRoute exact path="/dashboard" component={Dashboard} loading={loading} />
						<PrivateRoute exact path="/introduction" component={Introduction} loading={loading} />
						<PrivateRoute exact path="/segments/edit/:code" component={EditSegment} />
						<PrivateRoute exact path="/segments/edit/:code/facebook" component={ConnectFacebook} />
						<PrivateRoute exact path="/segments/edit/:code/google" component={ConnectGoogle} />
						<PrivateRoute exact path="/segments/edit/:code/platforms" component={ConnectPlatforms} />
						<PrivateRoute exact path="/plans" component={SubscribePlan} />
						<PrivateRoute exact path="/plans/subscribe/checkout/:planId" component={Checkout} />
						<PrivateRoute exact path="/plans/cancel/:planId" component={CancelPlan} />
						<PrivateRoute exact path="/plans/cancel/:planId/confirmation" component={AreYouSure} />
						<Route exact path="/support" component={Support} />
						<Route exact path="/signup" component={SignUp} />
						<Route exact path="/login" component={SignIn} />
						<Route exact path="/password-reset" component={PasswordReset} />
						<Redirect from="*" to="/404" />
					</Switch>
				</StyledInnerContainer>
			</StyledMainContainer>
		</div>
	);
};
export default React.memo(Main);
